import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Sparkles, CalendarRange } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BLANK = {
  code: "", name: "", tagline: "",
  base_price_monthly: 0, base_price_yearly: 0, currency: "INR",
  storage_gb: 10, max_users_default: 10,
  marketplace_access: false, support_level: "", trial_days: 14,
  features: [], highlight: false, active: true, sort_order: 99,
  trial_config: { mode: "days", days: 14, start_date: null, end_date: null, presets: [14, 30] },
};

export default function PlatformPlans() {
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(BLANK);

  const load = async () => { const { data } = await api.get("/platform/plans"); setPlans(data); };
  useEffect(() => { load(); }, []);

  const startEdit = (p) => {
    setEdit(p);
    setForm({ ...BLANK, ...p, features: p.features || [],
      trial_config: p.trial_config || { mode: "days", days: p.trial_days || 14, presets: [14, 30] } });
    setOpen(true);
  };
  const startNew = () => { setEdit(null); setForm(BLANK); setOpen(true); };

  const save = async () => {
    try {
      const { trial_config, ...rest } = form;
      const saved = edit
        ? (await api.patch(`/platform/plans/${edit.id}`, rest)).data
        : (await api.post("/platform/plans", rest)).data;
      // Persist trial_config via dedicated endpoint (writes to plan.trial_config for the public /register flow)
      try { await api.put(`/admin/platform-plans/${saved.code || form.code}/trial-config`, trial_config); }
      catch (tcErr) { toast.error(`Plan saved, but trial config failed: ${tcErr.response?.data?.detail || tcErr.message}`); }
      toast.success(edit ? "Plan updated" : "Plan created");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try { await api.delete(`/platform/plans/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="admin-platform-plans">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black flex items-center gap-2"><Sparkles className="w-7 h-7 text-primary" /> Platform Plans</h1>
          <p className="text-muted-foreground">Configure Basic / Premium / Enterprise — limits, features and pricing.</p>
        </div>
        <Button onClick={startNew} data-testid="plan-new"><Plus className="w-4 h-4 mr-1" /> New plan</Button>
      </div>

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead className="text-right">Monthly</TableHead>
            <TableHead className="text-right">Yearly</TableHead><TableHead className="text-right">Storage</TableHead>
            <TableHead>Active</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {plans.map((p) => (
              <TableRow key={p.id} data-testid={`plan-row-${p.code}`}>
                <TableCell className="font-mono text-xs">{p.code}</TableCell>
                <TableCell>
                  <div className="font-medium">{p.name} {p.highlight && <Badge className="ml-1 bg-orange-500">POPULAR</Badge>}</div>
                  <div className="text-xs text-muted-foreground">{p.tagline}</div>
                </TableCell>
                <TableCell className="text-right font-mono">₹{p.base_price_monthly.toLocaleString()}</TableCell>
                <TableCell className="text-right font-mono">₹{p.base_price_yearly.toLocaleString()}</TableCell>
                <TableCell className="text-right">{p.storage_gb} GB</TableCell>
                <TableCell><Badge variant={p.active ? "default" : "outline"}>{p.active ? "ACTIVE" : "INACTIVE"}</Badge></TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="outline" onClick={() => startEdit(p)} data-testid={`plan-edit-${p.code}`}><Edit3 className="w-3 h-3" /></Button>
                  <Button size="sm" variant="outline" onClick={() => remove(p)} data-testid={`plan-del-${p.code}`}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{edit ? "Edit" : "New"} plan</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} disabled={!!edit} data-testid="plan-form-code" /></div>
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="plan-form-name" /></div>
            <div className="col-span-2"><Label>Tagline</Label><Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} /></div>
            <div><Label>Monthly (₹)</Label><Input type="number" value={form.base_price_monthly} onChange={(e) => setForm({ ...form, base_price_monthly: parseFloat(e.target.value || "0") })} data-testid="plan-form-monthly" /></div>
            <div><Label>Yearly (₹)</Label><Input type="number" value={form.base_price_yearly} onChange={(e) => setForm({ ...form, base_price_yearly: parseFloat(e.target.value || "0") })} /></div>
            <div><Label>Storage (GB)</Label><Input type="number" value={form.storage_gb} onChange={(e) => setForm({ ...form, storage_gb: parseInt(e.target.value || "0") })} /></div>
            <div><Label>Default user cap</Label><Input type="number" value={form.max_users_default} onChange={(e) => setForm({ ...form, max_users_default: parseInt(e.target.value || "0") })} /></div>
            <div><Label>Trial (days · legacy)</Label><Input type="number" value={form.trial_days} onChange={(e) => setForm({ ...form, trial_days: parseInt(e.target.value || "0") })} /></div>
            <div><Label>Support level</Label><Input value={form.support_level} onChange={(e) => setForm({ ...form, support_level: e.target.value })} /></div>
            <div><Label>Sort order</Label><Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value || "0") })} /></div>
            <div className="col-span-2 rounded-xl border border-orange-400/30 bg-orange-500/5 p-3 space-y-3" data-testid="plan-trial-config">
              <div className="flex items-center gap-2 text-sm font-bold"><CalendarRange className="w-4 h-4 text-orange-500" /> Free-trial config <span className="text-[10px] font-normal text-muted-foreground">(shown as read-only to registrants)</span></div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Mode</Label>
                  <Select value={form.trial_config?.mode || "days"} onValueChange={(v) => setForm({ ...form, trial_config: { ...(form.trial_config || {}), mode: v } })}>
                    <SelectTrigger data-testid="plan-trial-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="days">Fixed days</SelectItem>
                      <SelectItem value="date_range">Date range</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.trial_config?.mode !== "date_range" ? (
                  <>
                    <div>
                      <Label className="text-xs">Days</Label>
                      <Input type="number" min="1" max="365" value={form.trial_config?.days || 14} onChange={(e) => setForm({ ...form, trial_config: { ...(form.trial_config || {}), days: parseInt(e.target.value || "0") } })} data-testid="plan-trial-days" />
                    </div>
                    <div>
                      <Label className="text-xs">Preset options (comma-separated)</Label>
                      <Input value={(form.trial_config?.presets || [14, 30]).join(",")} onChange={(e) => setForm({ ...form, trial_config: { ...(form.trial_config || {}), presets: e.target.value.split(",").map((s) => parseInt(s.trim())).filter(Boolean) } })} />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Label className="text-xs">Start date</Label>
                      <Input type="date" value={(form.trial_config?.start_date || "").slice(0, 10)} onChange={(e) => setForm({ ...form, trial_config: { ...(form.trial_config || {}), start_date: e.target.value ? new Date(e.target.value).toISOString() : null } })} data-testid="plan-trial-start" />
                    </div>
                    <div>
                      <Label className="text-xs">End date</Label>
                      <Input type="date" value={(form.trial_config?.end_date || "").slice(0, 10)} onChange={(e) => setForm({ ...form, trial_config: { ...(form.trial_config || {}), end_date: e.target.value ? new Date(e.target.value).toISOString() : null } })} data-testid="plan-trial-end" />
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="col-span-2"><Label>Features (one per line)</Label><Textarea rows={4} value={(form.features || []).join("\n")} onChange={(e) => setForm({ ...form, features: e.target.value.split("\n").filter(Boolean) })} /></div>
            <div className="flex items-center gap-2"><Switch checked={form.marketplace_access} onCheckedChange={(v) => setForm({ ...form, marketplace_access: v })} /><Label>Marketplace</Label></div>
            <div className="flex items-center gap-2"><Switch checked={form.highlight} onCheckedChange={(v) => setForm({ ...form, highlight: v })} /><Label>Highlight as POPULAR</Label></div>
            <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Active</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="plan-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
